import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  HostedAgentLifecycleAction,
  HostedAgentLifecycleImage,
  HostedAgentLifecycleRequest,
} from "@/api/admin-api"

import { getHostedAgentLifecycleErrorCode } from "@/api/admin-api"
import {
  Eraser,
  Play,
  RefreshCw,
  Rocket,
  ServerCog,
  Square,
  Undo2,
  XCircle,
} from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"

import { HostedAgentLifecycleStatusBadge } from "./hosted-agent-lifecycle-status-badge"
import { HostedAgentUpgradeDialog } from "./hosted-agent-upgrade-dialog"
import {
  isHostedAgentLifecycleActive,
  useAdminHostedAgentLifecycleRequestsQuery,
  useCancelHostedAgentLifecycleRequestMutation,
  useCreateHostedAgentLifecycleRequestMutation,
} from "../hooks/admin-queries"
import {
  describeHostedAgentLifecycleImage,
  HOSTED_AGENT_LIFECYCLE_ACTION_DEFAULTS,
} from "../utils/hosted-agent-lifecycle"
import { formatHostedAgentTimestamp } from "../utils/hosted-agents"

/**
 * Lifecycle tab (B10/B13): "Upgrade now", restart, stop, start and rollback
 * each queue ONE lifecycle request; the deploy worker on the customer VM
 * executes it and reports back. The panel polls the history while a request
 * is non-terminal and lets the admin cancel a request that is still pending.
 */
export function HostedAgentLifecyclePanel({
  agentId,
  agentName,
  currentVersion,
}: {
  agentId: string
  agentName: string
  currentVersion: string | null
}) {
  const { t } = useTranslation()
  const requestsQuery = useAdminHostedAgentLifecycleRequestsQuery(agentId)
  const createMutation = useCreateHostedAgentLifecycleRequestMutation()
  const cancelMutation = useCancelHostedAgentLifecycleRequestMutation()
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false)
  const [isResetOpen, setIsResetOpen] = useState(false)
  const [isDecommissionOpen, setIsDecommissionOpen] = useState(false)

  const requests = requestsQuery.data ?? []
  const activeRequest = requests.find((request) => isHostedAgentLifecycleActive(request.status))
  const isBusy = createMutation.isPending || cancelMutation.isPending
  const actionsDisabled = Boolean(activeRequest) || isBusy || requestsQuery.isLoading

  const describeError = (error: unknown) => {
    const code = getHostedAgentLifecycleErrorCode(error)
    if (code === "lifecycle_request_in_flight") {
      return t("adminDashboard.hostedAgents.lifecycle.errors.inFlight", {
        defaultValue:
          "A lifecycle request is already in flight — for upgrades, only one agent at a time across the tenant.",
      })
    }
    if (code === "not_cancellable") {
      return t("adminDashboard.hostedAgents.lifecycle.errors.notCancellable", {
        defaultValue: "The worker already picked this request up; it can no longer be cancelled.",
      })
    }
    return error instanceof Error ? error.message : undefined
  }

  const queue = async (action: HostedAgentLifecycleAction, image?: HostedAgentLifecycleImage) => {
    try {
      await createMutation.mutateAsync({
        agentId,
        input: image ? { action, image } : { action },
      })
      toast.success(
        t("adminDashboard.hostedAgents.lifecycle.queued", {
          defaultValue: "{{action}} queued — the deploy worker will pick it up.",
          action: t(`adminDashboard.hostedAgents.lifecycle.actions.${action}`, {
            defaultValue: HOSTED_AGENT_LIFECYCLE_ACTION_DEFAULTS[action],
          }),
        })
      )
      return true
    } catch (error) {
      toast.error(
        t("adminDashboard.hostedAgents.lifecycle.queueError", {
          defaultValue: "Failed to queue the request",
        }),
        { description: describeError(error) }
      )
      return false
    }
  }

  const handleCancel = async (requestId: string) => {
    try {
      await cancelMutation.mutateAsync({ agentId, requestId })
      toast.success(
        t("adminDashboard.hostedAgents.lifecycle.cancelled", { defaultValue: "Request cancelled" })
      )
    } catch (error) {
      toast.error(
        t("adminDashboard.hostedAgents.lifecycle.cancelError", {
          defaultValue: "Failed to cancel the request",
        }),
        { description: describeError(error) }
      )
    }
  }

  return (
    <div className="space-y-4">
      <Alert>
        <ServerCog className="size-4" />
        <AlertTitle>
          {t("adminDashboard.hostedAgents.lifecycle.title", {
            defaultValue: "Container lifecycle",
          })}
        </AlertTitle>
        <AlertDescription>
          {t("adminDashboard.hostedAgents.lifecycle.help", {
            defaultValue:
              "These actions are executed by the deploy worker on the customer VM — the portal only queues a request and follows its progress. Upgrades pause and drain the agent, recreate the container, verify it and roll back automatically if verification fails. The container holds no durable state: a restart drops its in-memory caches, which are rebuilt automatically on the next run. Reset additionally guarantees that all derived local state is wiped — authoritative state in the backend is never touched. Stop and start do not change whether the agent is enabled.",
          })}
        </AlertDescription>
      </Alert>

      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={actionsDisabled} type="button" onClick={() => setIsUpgradeOpen(true)}>
          <Rocket className="size-4" />
          {t("adminDashboard.hostedAgents.lifecycle.upgradeNow", { defaultValue: "Upgrade now" })}
        </Button>
        <Button
          disabled={actionsDisabled}
          type="button"
          variant="outline"
          onClick={() => void queue("restart")}
        >
          <RefreshCw className="size-4" />
          {t("adminDashboard.hostedAgents.lifecycle.actions.restart", { defaultValue: "Restart" })}
        </Button>
        <Button
          disabled={actionsDisabled}
          type="button"
          variant="outline"
          onClick={() => void queue("stop")}
        >
          <Square className="size-4" />
          {t("adminDashboard.hostedAgents.lifecycle.actions.stop", { defaultValue: "Stop" })}
        </Button>
        <Button
          disabled={actionsDisabled}
          type="button"
          variant="outline"
          onClick={() => void queue("start")}
        >
          <Play className="size-4" />
          {t("adminDashboard.hostedAgents.lifecycle.actions.start", { defaultValue: "Start" })}
        </Button>
        <Button
          disabled={actionsDisabled}
          type="button"
          variant="outline"
          onClick={() => setIsResetOpen(true)}
        >
          <Eraser className="size-4" />
          {t("adminDashboard.hostedAgents.lifecycle.actions.reset", { defaultValue: "Reset" })}
        </Button>
        <Button
          disabled={actionsDisabled}
          type="button"
          variant="ghost"
          onClick={() => void queue("rollback")}
        >
          <Undo2 className="size-4" />
          {t("adminDashboard.hostedAgents.lifecycle.actions.rollback", {
            defaultValue: "Rollback",
          })}
        </Button>
      </div>

      <Button
        disabled={actionsDisabled}
        type="button"
        variant="outline"
        onClick={() => setIsDecommissionOpen(true)}
      >
        {t("adminDashboard.hostedAgents.lifecycle.actions.decommission", {
          defaultValue: "Remove running environment",
        })}
      </Button>
      <AlertDialog
        open={isDecommissionOpen}
        onOpenChange={(open) => !isBusy && setIsDecommissionOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("adminDashboard.hostedAgents.lifecycle.decommissionDialog.title", {
                defaultValue: "Remove the running environment for {{name}}?",
                name: agentName,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("adminDashboard.hostedAgents.lifecycle.decommissionDialog.body", {
                defaultValue:
                  "This stops the agent and removes its running environment. Reports, history and the saved agent definition are retained. Wait for successful removal before deleting the agent registration.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>
              {t("adminDashboard.hostedAgents.lifecycle.resetDialog.cancel", {
                defaultValue: "Cancel",
              })}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isBusy}
              onClick={(event) => {
                event.preventDefault()
                void queue("decommission").then((queued) => {
                  if (queued) setIsDecommissionOpen(false)
                })
              }}
            >
              {t("adminDashboard.hostedAgents.lifecycle.decommissionDialog.confirm", {
                defaultValue: "Remove environment, keep data",
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {activeRequest ? (
        <ActiveRequestCard
          isCancelling={cancelMutation.isPending}
          request={activeRequest}
          onCancel={() => void handleCancel(activeRequest.id)}
        />
      ) : null}

      <div className="space-y-2 border-t pt-3">
        <p className="text-sm font-medium">
          {t("adminDashboard.hostedAgents.lifecycle.historyTitle", { defaultValue: "History" })}
        </p>
        <LifecycleHistory
          isError={requestsQuery.isError}
          isLoading={requestsQuery.isLoading}
          requests={requests}
        />
      </div>

      {/* #3026: reset is the ONLY explicit "wipe derived local state" contract.
          It queues like every other action but always behind a confirmation. */}
      <AlertDialog open={isResetOpen} onOpenChange={(open) => !isBusy && setIsResetOpen(open)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Eraser className="size-5" />
              {t("adminDashboard.hostedAgents.lifecycle.resetDialog.title", {
                defaultValue: "Reset {{name}}",
                name: agentName,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("adminDashboard.hostedAgents.lifecycle.resetDialog.body", {
                defaultValue:
                  "The deploy worker recreates the container and wipes all derived local state — caches, temporary files and warmed skills. Authoritative state in the backend (runs, threads, memory, configuration) is not touched; the agent rebuilds what it needs on the next run. Today a restart already drops this state, since the container keeps nothing durable — Reset makes the wipe an explicit guarantee.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>
              {t("adminDashboard.hostedAgents.lifecycle.resetDialog.cancel", {
                defaultValue: "Cancel",
              })}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isBusy}
              onClick={(event) => {
                event.preventDefault()
                void (async () => {
                  if (await queue("reset")) setIsResetOpen(false)
                })()
              }}
            >
              {createMutation.isPending ? (
                <Spinner className="size-4" />
              ) : (
                <Eraser className="size-4" />
              )}
              {t("adminDashboard.hostedAgents.lifecycle.resetDialog.confirm", {
                defaultValue: "Queue reset",
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <HostedAgentUpgradeDialog
        agentName={agentName}
        currentVersion={currentVersion}
        isOpen={isUpgradeOpen}
        isPending={createMutation.isPending}
        onOpenChange={setIsUpgradeOpen}
        onConfirm={async (image) => {
          if (await queue("upgrade", image)) setIsUpgradeOpen(false)
        }}
      />
    </div>
  )
}

function LifecycleHistory({
  isLoading,
  isError,
  requests,
}: {
  isLoading: boolean
  isError: boolean
  requests: HostedAgentLifecycleRequest[]
}) {
  const { t } = useTranslation()
  if (isLoading) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <Spinner className="size-3" />
        {t("adminDashboard.hostedAgents.lifecycle.historyLoading", {
          defaultValue: "Loading requests…",
        })}
      </p>
    )
  }
  if (isError) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.lifecycle.historyUnavailable", {
          defaultValue: "Lifecycle history is unavailable — the backend may predate this feature.",
        })}
      </p>
    )
  }
  if (requests.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.lifecycle.historyEmpty", {
          defaultValue: "No lifecycle requests yet.",
        })}
      </p>
    )
  }
  return (
    <ul className="space-y-1.5">
      {requests.map((request) => (
        <LifecycleRequestRow key={request.id} request={request} />
      ))}
    </ul>
  )
}

function ActiveRequestCard({
  request,
  isCancelling,
  onCancel,
}: {
  request: HostedAgentLifecycleRequest
  isCancelling: boolean
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const recentLog = request.log.slice(-5)
  return (
    <div className="bg-muted/40 space-y-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <HostedAgentLifecycleStatusBadge action={request.action} status={request.status} />
          {request.image ? (
            <span className="text-muted-foreground font-mono text-xs">
              {describeHostedAgentLifecycleImage(request.image)}
            </span>
          ) : null}
          {request.claimed_by ? (
            <span className="text-muted-foreground text-xs">
              {t("adminDashboard.hostedAgents.lifecycle.claimedBy", {
                defaultValue: "Worker: {{worker}}",
                worker: request.claimed_by,
              })}
            </span>
          ) : null}
        </div>
        {request.status === "pending" ? (
          <Button
            disabled={isCancelling}
            size="sm"
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            {isCancelling ? <Spinner className="size-4" /> : <XCircle className="size-4" />}
            {t("adminDashboard.hostedAgents.lifecycle.cancel", { defaultValue: "Cancel request" })}
          </Button>
        ) : null}
      </div>
      {request.status === "pending" ? (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.lifecycle.waitingForWorker", {
            defaultValue: "Waiting for the deploy worker on the customer VM to claim the request…",
          })}
        </p>
      ) : null}
      {recentLog.length > 0 ? (
        <ul className="space-y-0.5 font-mono text-[11px]">
          {recentLog.map((entry, index) => (
            <li
              key={`${entry.at}-${index}`}
              className={
                entry.level === "error"
                  ? "bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1"
                  : "text-muted-foreground"
              }
            >
              [{entry.step}] {entry.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function LifecycleRequestRow({ request }: { request: HostedAgentLifecycleRequest }) {
  const { t, i18n } = useTranslation()
  const when = formatHostedAgentTimestamp(
    request.finished_at ?? request.requested_at,
    i18n.language
  )
  const image = describeHostedAgentLifecycleImage(request.image)
  return (
    <li className="flex flex-wrap items-center gap-2 text-xs">
      <HostedAgentLifecycleStatusBadge action={request.action} status={request.status} />
      <span className="text-muted-foreground">{when ?? "—"}</span>
      {image ? <span className="text-muted-foreground font-mono">· {image}</span> : null}
      {request.result?.code && !isHostedAgentLifecycleActive(request.status) ? (
        <span className="text-muted-foreground font-mono">· {request.result.code}</span>
      ) : null}
      {request.detail ? (
        <span className="text-muted-foreground max-w-md truncate" title={request.detail}>
          · {request.detail}
        </span>
      ) : null}
      {request.requested_by ? (
        <span className="text-muted-foreground">
          ·{" "}
          {t("adminDashboard.hostedAgents.lifecycle.requestedBy", {
            defaultValue: "by {{who}}",
            who: request.requested_by,
          })}
        </span>
      ) : null}
    </li>
  )
}
